import {AccountApplication} from '@application/Account';
import {ConfigurationApplication} from '@application/Config';
import {ShippingAddressCommand} from '@domain/model/Account/Address/domain/model/ShippingAddress';
import {ActiveUserAddress} from '@domain/model/Account/UserAddress/ActiveUserAddress';
import {UpdateUserAddressCommand} from '@domain/model/Account/UserAddress/UpdateUserAddressCommand';
import {Country} from '@domain/model/Site/Locale';
import {Segment} from '@domain/model/User/Segment';
import {sendLogMessage} from '@infra/domain/model/ServerLog/ServerLogRepository';

type ShippingCommandWithoutCountry = Omit<ShippingAddressCommand, 'country'>;

type ShippingAddressInfo = {
  address?: Record<string, unknown>;
  delivery?: {courier?: string | null; pickupId?: string | null; pickupName?: string | null};
};

const toUpdateUserAddressCommand = ({
  shippingAddress,
  country,
  userId,
  email,
  segment,
}: {
  shippingAddress: ShippingCommandWithoutCountry;
  country: Country;
  userId: string | null;
  email: string | null;
  segment: Segment | null;
}): UpdateUserAddressCommand => {
  const tradeName = ConfigurationApplication.retrieve().tradename;

  return {
    userId: userId || null,
    name: shippingAddress.name || null,
    surname: shippingAddress.lastName || null,
    address: shippingAddress.address || null,
    postalCode: shippingAddress.postCode || null,
    cityName: shippingAddress.city || null,
    countryCode: country || null,
    phoneNumber: shippingAddress.phone || null,
    email: email || null,
    carrierCode: shippingAddress.courier || null,
    carrierPudoReference: shippingAddress.pickupId || null,
    segment: segment || null,
    tradeName: tradeName || null,
  };
};

const saveShippingAddressWithFallback = async ({
  userAddressRequest,
  shippingAddress,
  country,
}: {
  userAddressRequest: UpdateUserAddressCommand;
  shippingAddress: ShippingCommandWithoutCountry;
  country: Country;
}): Promise<Record<string, boolean> | null> => {
  try {
    const result = await AccountApplication.setUserAddress(userAddressRequest);

    if (result) {
      return result;
    }
  } catch {
    void 0; // Fall back to the legacy BFU flow when Logistics is unavailable.
  }

  return AccountApplication.setShippingAddress({...shippingAddress, country});
};

const toShippingAddressInfo = ({
  activeUserAddress,
  shippingAddress,
}: {
  activeUserAddress: ActiveUserAddress;
  shippingAddress: ShippingAddressInfo;
}): ShippingAddressInfo => {
  const {name, surname, address, postalCode, cityName, phoneNumber, carrierCode, carrierPudoReference} =
    activeUserAddress;
  const legacyDelivery = shippingAddress?.delivery || {};
  const pickupName =
    carrierPudoReference && legacyDelivery.pickupId === carrierPudoReference ? legacyDelivery.pickupName : undefined;

  return {
    address: {
      name,
      lastName: surname,
      address,
      postCode: postalCode,
      city: cityName,
      phone: phoneNumber,
    },
    delivery: carrierPudoReference
      ? {
          courier: carrierCode,
          pickupId: carrierPudoReference,
          pickupName,
        }
      : undefined,
  };
};

const getActiveShippingAddressWithFallback = async ({
  userId,
  shippingAddress,
}: {
  userId: string | null;
  shippingAddress: ShippingAddressInfo;
}): Promise<ShippingAddressInfo> => {
  try {
    const activeUserAddress = await AccountApplication.viewActiveUserAddressByUserId({userId: userId as string});

    if (activeUserAddress) {
      return toShippingAddressInfo({activeUserAddress, shippingAddress});
    }

    if (userId) {
      void sendLogMessage(`viewActiveUserAddressByUserId returned no address for user "${userId}", using BFU data`);
    }
  } catch (error) {
    const detail = error instanceof Error ? error.message : JSON.stringify(error);
    void sendLogMessage(
      `viewActiveUserAddressByUserId failed for user "${userId}", falling back to BFU data: ${detail}`,
    );
  }

  return shippingAddress;
};

export {toUpdateUserAddressCommand, saveShippingAddressWithFallback, getActiveShippingAddressWithFallback};
